import { isOnPrem } from '@/utils/buildProfile';

import {
  mapLocaleAliasToSupportedLocale,
  type MapLocaleToSupportedOptions,
  type SupportedLocale,
} from './supportedLocales';

/**
 * The literal token Next.js inlines at build time. `onprem/onprem-runtime-config.js`
 * swaps it for the real value at container start, but deliberately leaves it in
 * place when the deployment configured nothing — so seeing it here means
 * "unconfigured", not "broken".
 */
const PLACEHOLDER_TOKEN = '__ONPREM_DEFAULT_LOCALE__';

/**
 * The display language this on-prem deployment was configured with, if any.
 *
 * Sourced from `ONPREM_ORG_DISPLAY_LANGUAGE` — the same variable ml-scribe reads —
 * so the login screen matches the app the user lands in. Needed pre-auth: before
 * sign-in there is no user document to read a language from, and on-prem the
 * build strips Next's i18n config so `router.locale` is `undefined` too.
 *
 * Ranks *below* the OS/browser locale in the resolution chain: the browser is
 * the authority on a user's language, and this only fills the gap where the OS
 * expressed nothing. Matches the backend's ordering in
 * `UserService._resolve_display_language`.
 *
 * Returns `undefined` unless this is an on-prem build with a configured,
 * supported language. Cloud always returns `undefined`.
 */
export const getDeploymentDefaultLocale = (
  options?: MapLocaleToSupportedOptions
): SupportedLocale | undefined => {
  // Plain const, so cloud bundles dead-code-eliminate everything below.
  if (!isOnPrem) {
    return undefined;
  }

  const raw = process.env.NEXT_PUBLIC_ONPREM_DEFAULT_LOCALE;
  if (!raw || raw === PLACEHOLDER_TOKEN) {
    return undefined;
  }

  // Unsupported or malformed values resolve to `undefined` rather than the app
  // default, so the caller's own fallback chain still runs.
  return mapLocaleAliasToSupportedLocale(raw, options);
};
